// Marquee (rubber-band) multi-select hit test for TouchTour Studio (WP-L).
//
// `marqueeSelect` is a pure, dependency-free function: given the board's nodes
// and a selection rectangle in *world* (grid) coordinates, it returns the ids of
// the nodes whose centre falls inside the rectangle. It does not mutate its input
// and does not touch React/canvas internals, so it is trivially unit-testable
// (mirroring tidyBoard.js).
//
// The rectangle is normalised here, so callers can pass the raw drag start/end
// corners in any order:
//
//   import { marqueeSelect } from "./studio/marqueeSelect";
//
//   const ids = marqueeSelect(tour.images, {
//     x1: startWorld.x, y1: startWorld.y,
//     x2: endWorld.x,   y2: endWorld.y,
//   });
//
// `images` may be either the canvas's id→image map (`tour.images`) or a plain
// array of image objects; each node only needs an `id` and its `x`/`y`.

// Normalise the `images` argument (id→image map *or* array) into an array.
const toImageArray = images => {
  if (Array.isArray(images)) return images.filter(Boolean);
  if (images && typeof images === "object") return Object.values(images);
  return [];
};

const isFiniteNumber = value =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Return the ids of nodes whose centre lies within the marquee rectangle.
 *
 * The rectangle is given by two opposite corners (in any order) in world/grid
 * coordinates; it is normalised internally so dragging in any direction works.
 * Selection is inclusive of the edges. A degenerate (zero-area) rectangle simply
 * selects any node centred exactly on that point — callers that want a plain
 * "click clears selection" should treat a non-drag as empty themselves.
 *
 * @param {Object<string,Object>|Array<Object>} images - id→image map or array.
 * @param {{x1:number,y1:number,x2:number,y2:number}} rect - opposite corners.
 * @returns {string[]} ids of the nodes inside the rectangle.
 */
export const marqueeSelect = (images, rect) => {
  if (!rect) return [];

  const { x1, y1, x2, y2 } = rect;
  if (![x1, y1, x2, y2].every(isFiniteNumber)) return [];

  const minX = Math.min(x1, x2);
  const maxX = Math.max(x1, x2);
  const minY = Math.min(y1, y2);
  const maxY = Math.max(y1, y2);

  return toImageArray(images)
    .filter(
      img =>
        img != null &&
        img.id != null &&
        isFiniteNumber(img.x) &&
        isFiniteNumber(img.y) &&
        img.x >= minX &&
        img.x <= maxX &&
        img.y >= minY &&
        img.y <= maxY,
    )
    .map(img => img.id);
};

export default marqueeSelect;
